import React, { useEffect, useState, useRef } from 'react';
import { TrendingUp, ShoppingCart, Package, Wallet, AlertTriangle, Euro, ChevronLeft, ChevronRight, ListChecks, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Navigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { puoFare } from '../utils/permessi';
import CalendarioImpegni from '../components/CalendarioImpegni';
import TodoList from '../components/TodoList';
import AndamentoTornei from '../components/AndamentoTornei';

const fmt = (n) => `€${(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const now = new Date();
const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
// Due pagine affiancate che scorrono: 0 = operativa (to-do + calendario), 1 = contabile (KPI, ordini, scorte)
const PAGINE = [{ label: 'OPERATIVA', icon: ListChecks }, { label: 'CONTABILE', icon: BarChart3 }];
const leggiPagina = () => { try { return +localStorage.getItem('dashboard_pagina') === 1 ? 1 : 0; } catch { return 0; } };

// Prima pagina permessa, per chi non ha nessuna sezione della dashboard (es. Consulente paghe → Dipendenti)
const PRIMA_PAGINA = [
  ['dipendenti','/dipendenti'], ['presenze','/dipendenti/calendario'], ['ordini','/orders'], ['prodotti','/products/catalog'],
  ['clienti','/customers'], ['fornitori','/suppliers'], ['planning','/planning'], ['pagamento_corsi','/corsisti'], ['contabilita_corsi','/contabilita-corsi'], ['ricevute_asd','/ricevute-asd'], ['campi','/campi'],
  ['banca','/cash/banca'], ['cassa','/cash/centri'], ['segreteria','/segreteria'], ['tesseramenti','/tesseramenti'],
  ['impostazioni','/settings'], ['dashboard_cliente','/settings/widgets'], ['password_manager','/password-manager'],
  ['changelog','/changelog'], ['guida','/guida'],
];

export default function Dashboard() {
  const { user } = useAuth();
  const vedeCompiti    = puoFare(user, 'compiti', 'view');
  const vedeCalendario = puoFare(user, 'calendario', 'view');
  const haOperativa    = vedeCompiti || vedeCalendario;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagina, setPagina] = useState(leggiPagina);
  const touchX = useRef(null);
  const vai = (p) => { setPagina(p); try { localStorage.setItem('dashboard_pagina', String(p)); } catch {} };

  useEffect(() => {
    api.get('/dashboard').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={s.loading}>Caricamento...</div>;

  // Il server manda null per i dati dei moduli senza permesso (vedi dashboard.service.ts): si nascondono
  const vede = (k) => data?.[k] !== null && data?.[k] !== undefined;
  const kpis = [
    { k: 'fatturato_mese',   label: 'FATTURATO MESE',   value: fmt(data?.fatturato_mese),  icon: TrendingUp, color: '#22C55E' },
    { k: 'margine_mese',     label: 'MARGINE MESE',     value: fmt(data?.margine_mese),    icon: Euro,        color: '#3B82F6' },
    { k: 'ordini_mese',      label: 'ORDINI MESE',      value: data?.ordini_mese ?? 0,     icon: ShoppingCart,color: '#F59E0B' },
    { k: 'saldo_cassa',      label: 'SALDO CONTI',      value: fmt(data?.saldo_cassa),     icon: Wallet,      color: '#8B5CF6' },
    { k: 'valore_magazzino', label: 'VALORE MAGAZZINO', value: fmt(data?.valore_magazzino),icon: Package,     color: '#F4600C' },
  ].filter(x => vede(x.k));
  const vedeOrdini = vede('debitori');
  const vedeTornei = puoFare(user, 'tornei', 'view');
  const haContabile = kpis.length > 0 || vedeOrdini || vedeTornei || (data?.alerts_scorta?.length > 0);
  if (!haOperativa && !haContabile) {
    const prima = PRIMA_PAGINA.find(([m]) => puoFare(user, m, 'view'));
    return prima ? <Navigate to={prima[1]} replace/> : <div style={s.loading}>Nessuna sezione abilitata per il tuo ruolo: contatta l'amministratore.</div>;
  }
  const dueP = haOperativa && haContabile;
  const paginaVista = !haOperativa ? 1 : (haContabile ? pagina : 0);

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>DASHBOARD</h1>
          <p style={s.sub}>{paginaVista === 0 ? 'Cosa c\'è da fare e cosa c\'è in calendario' : 'Andamento economico del mese'}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
          {dueP && <>
          <button className="solo-computer" style={s.arrow} disabled={paginaVista === 0} onClick={() => vai(0)}><ChevronLeft size={16} /></button>
          <div style={s.tabs}>
            {PAGINE.map(({ label, icon: Icon }, i) => (
              <button key={label} style={{ ...s.tab, ...(paginaVista === i ? s.tabOn : {}) }} onClick={() => vai(i)}>
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
          <button className="solo-computer" style={s.arrow} disabled={paginaVista === 1} onClick={() => vai(1)}><ChevronRight size={16} /></button>
          </>}
          <div className="solo-computer" style={s.dateBadge}>{MESI[now.getMonth()].toUpperCase()} {now.getFullYear()}</div>
        </div>
      </div>

      <div style={{ overflow: 'hidden' }}
        onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={e => {
          if (touchX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchX.current; touchX.current = null;
          if (dx < -60 && paginaVista === 0 && dueP) vai(1); else if (dx > 60 && paginaVista === 1) vai(0);
        }}>
      <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-start', transform: `translateX(-${paginaVista * 100}%)`, transition: 'transform 0.35s cubic-bezier(.4,0,.2,1)' }}>

      {/* ── PAGINA 1: OPERATIVA ── */}
      <div style={{ ...s.pagina, ...(paginaVista === 0 ? {} : s.paginaNascosta) }}>
        <div style={s.gridOperativa}>
          {vedeCompiti && <TodoList />}
          {vedeCalendario && <CalendarioImpegni />}
        </div>
      </div>

      {/* ── PAGINA 2: CONTABILE (solo se almeno un dato è permesso) ── */}
      {haContabile && <div style={{ ...s.pagina, ...(paginaVista === 1 ? {} : s.paginaNascosta) }}>
      {/* ALERT SCORTA */}
      {data?.alerts_scorta?.length > 0 && (
        <div style={s.alertBanner}>
          <AlertTriangle size={16} color="#F59E0B" />
          <span style={{ fontWeight: '700', color: '#F59E0B' }}>SCORTA MINIMA:</span>
          <span style={{ color: 'var(--text)', fontSize: 'var(--font-size-sm)' }}>
            {data.alerts_scorta.slice(0, 3).map(a => `${a.prodotto} ${a.taglia} (${a.disponibile} pz)`).join(' · ')}
            {data.alerts_scorta.length > 3 && ` +${data.alerts_scorta.length - 3} altri`}
          </span>
        </div>
      )}

      {/* KPI */}
      <div style={s.kpiGrid}>
        {kpis.map(({ label, value, icon: Icon, color }) => (
          <div key={label} style={s.kpiCard}>
            <div style={{ ...s.kpiIcon, background: `${color}18` }}>
              <Icon size={20} color={color} />
            </div>
            <div style={s.kpiValue}>{value}</div>
            <div style={s.kpiLabel}>{label}</div>
          </div>
        ))}
      </div>

      {vedeTornei && <AndamentoTornei />}

      {vedeOrdini && <div style={s.grid2}>
        {/* TOP PRODOTTI */}
        <div style={s.card}>
          <div style={s.cardTitle}>TOP PRODOTTI DEL MESE</div>
          {data?.top_prodotti?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.top_prodotti} layout="vertical" margin={{ left: 0, right: 20 }}>
                <XAxis type="number" tick={{ fill: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }} />
                <YAxis type="category" dataKey="nome" tick={{ fill: '#aaa', fontSize: 'var(--font-size-xs)' }} width={120} />
                <Tooltip contentStyle={{ background: 'var(--border)', border: '1px solid var(--border2)', borderRadius: '8px' }} formatter={(v) => [`${v} pz`, 'Quantità']} />
                <Bar dataKey="_sum.quantita" radius={[0, 4, 4, 0]}>
                  {data.top_prodotti.map((_, i) => <Cell key={i} fill={i === 0 ? '#F4600C' : 'var(--text-dim)'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={s.empty}>Nessun ordine questo mese</div>}
        </div>

        {/* DEBITORI */}
        <div style={s.card}>
          <div style={s.cardTitle}>ORDINI NON PAGATI</div>
          {data?.debitori?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {data.debitori.map((d) => (
                <div key={d.id} style={s.debitoreRow}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: 'var(--font-size-sm)' }}>{d.cliente}</div>
                    <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>{d.ordine}</div>
                  </div>
                  <div style={{ color: '#EF4444', fontWeight: '700', fontSize: 'var(--font-size-sm)' }}>{fmt(d.totale)}</div>
                </div>
              ))}
            </div>
          ) : <div style={s.empty}>Nessun ordine in sospeso 🎉</div>}
        </div>
      </div>}

      {/* ALERT SCORTA DETTAGLIO */}
      {data?.alerts_scorta?.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}><AlertTriangle size={14} color="#F59E0B" style={{ marginRight: '8px' }} />VARIANTI SOTTO SCORTA MINIMA</div>
          <div style={s.alertGrid}>
            {data.alerts_scorta.map(a => (
              <div key={a.variant_id} style={s.alertCard}>
                <div style={s.alertSku}>{a.sku}</div>
                <div style={s.alertProdotto}>{a.prodotto}</div>
                <div style={s.alertTaglia}>{a.taglia}</div>
                <div style={s.alertQty}>
                  <span style={{ color: '#EF4444', fontWeight: '800', fontSize: 'var(--font-size-xl)' }}>{a.disponibile}</span>
                  <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>/ min {a.scorta_minima}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>}
      </div>
      </div>
    </div>
  );
}

const s = {
  pagina: { width: '100%', flexShrink: 0, minWidth: 0 },
  paginaNascosta: { height: 0, overflow: 'hidden', visibility: 'hidden' },
  gridOperativa: { display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr) minmax(0, 1.6fr)', gap: '16px', alignItems: 'start' },
  tabs: { display: 'flex', flexWrap: 'nowrap', gap: '4px', background: 'var(--bg-input)', border: '1px solid var(--border,#222)', borderRadius: '10px', padding: '3px' },
  tab: { display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', borderRadius: '7px', padding: '7px 14px', fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '1.5px', color: 'var(--text-dim)', cursor: 'pointer' },
  tabOn: { background: '#F4600C', color: '#fff' },
  arrow: { background: 'var(--bg-input)', border: '1px solid var(--border,#222)', borderRadius: '8px', padding: '7px', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh', color: 'var(--text-dim)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-title)', fontWeight: '800', letterSpacing: '2px' },
  sub: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', marginTop: '4px' },
  dateBadge: { background: 'var(--bg-input)', border: '1px solid #222', borderRadius: '8px', padding: '10px 16px', fontSize: 'var(--font-size-xs)', fontWeight: '700', letterSpacing: '2px', color: 'var(--text-muted)' },
  alertBanner: { display: 'flex', alignItems: 'center', gap: '10px', background: '#F59E0B10', border: '1px solid #F59E0B30', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', flexWrap: 'wrap' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' },
  kpiCard: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 16px' },
  kpiIcon: { width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' },
  kpiValue: { fontSize: 'var(--font-size-xl)', fontWeight: '800', marginBottom: '6px', fontFamily: "'Barlow Condensed', sans-serif" },
  kpiLabel: { fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', letterSpacing: '1.5px', fontWeight: '700' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px' },
  cardTitle: { fontSize: 'var(--font-size-xs)', fontWeight: '700', letterSpacing: '2px', color: 'var(--text-muted)', marginBottom: '16px', display: 'flex', alignItems: 'center' },
  empty: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: '32px 0' },
  debitoreRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--bg-input)', borderRadius: '8px' },
  alertGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' },
  alertCard: { background: 'var(--bg-input)', border: '1px solid #F59E0B30', borderRadius: '8px', padding: '12px' },
  alertSku: { fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', fontWeight: '700', letterSpacing: '1px', marginBottom: '4px' },
  alertProdotto: { fontSize: 'var(--font-size-sm)', fontWeight: '600', marginBottom: '2px' },
  alertTaglia: { fontSize: 'var(--font-size-xs)', color: '#F4600C', fontWeight: '700', marginBottom: '8px' },
  alertQty: { display: 'flex', alignItems: 'baseline', gap: '4px' },
};
